import React, { useState, useEffect, useRef } from 'react';
import { BotMessageSquare } from 'lucide-react';
import { useChatContext } from '@/context/chat-context';
import { getChatbotHealth } from '@/api/chatbot';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/authStore';

interface ChatToggleButtonProps {
  className?: string;
}

const DRAG_THRESHOLD = 5;

export const ChatToggleButton: React.FC<ChatToggleButtonProps> = ({ className }) => {
  const { isOpen, toggleChat, unreadCount } = useChatContext();
  const { user } = useAuth();
  const isSuperAdmin = Boolean((user as any)?.is_super_admin) || user?.role === 'super_admin' || user?.role === 'super-admin';
  
  const [isAiAvailable, setIsAiAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [mouseDownPos, setMouseDownPos] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSuperAdmin) return;
    
    const checkAvailability = async () => {
      try {
        const health = await getChatbotHealth();
        const available = (
          health.status === 'healthy' &&
          !!health.cabinet_enabled &&
          (health.ai_quota?.enabled ?? true)
        );
        setIsAiAvailable(available);
      } catch (error) {
        console.error('Failed to check AI availability:', error);
        setIsAiAvailable(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAvailability();
  }, [isSuperAdmin]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setHasMoved(false);
    setMouseDownPos({ x: e.clientX, y: e.clientY });
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = Math.abs(e.clientX - mouseDownPos.x);
      const deltaY = Math.abs(e.clientY - mouseDownPos.y);
      
      if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
        setHasMoved(true);
      }

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      const maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 64);
      const maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 64);

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, mouseDownPos]);

  if (isSuperAdmin) return null;
  if (isOpen || isLoading || !isAiAvailable) return null;

  const isPositioned = position.x !== 0 || position.y !== 0;

  return (
    <div
      ref={buttonRef}
      className={cn(
        isPositioned ? "fixed" : "fixed bottom-6 right-6",
        "z-40",
        !isPositioned && "sm:bottom-8 sm:right-8",
        isDragging && "cursor-grabbing",
        className
      )}
      style={isPositioned ? {
        left: `${position.x}px`,
        top: `${position.y}px`,
      } : undefined}
    >
      <button
        onMouseDown={handleMouseDown}
        onClick={() => {
          if (!hasMoved) {
            toggleChat();
          }
        }}
        className={cn(
          "group relative flex items-center justify-center",
          "h-14 w-14 sm:h-16 sm:w-16",
          "rounded-full",
          "bg-[rgb(var(--brand-navy-rgb))]",
          "hover:bg-[rgba(var(--brand-navy-rgb),0.9)]",
          "shadow-legal-lg",
          "hover:shadow-legal-xl",
          "transition-all duration-300 ease-out",
          "hover:scale-105 active:scale-95",
          "focus:outline-none focus:ring-4 focus:ring-[rgba(var(--brand-navy-rgb),0.2)]",
          "border-2 border-[rgba(var(--brand-gold-rgb),0.2)]",
          "hover:border-[rgba(var(--brand-gold-rgb),0.4)]",
          isDragging ? "cursor-grabbing" : "cursor-pointer"
        )}
        aria-label="Open AI Assistant"
      >
        {/* Icon container */}
        <div className="relative z-10">
          <BotMessageSquare className="w-7 h-7 sm:w-8 sm:h-8 text-[rgb(var(--brand-gold-rgb))] transition-transform duration-300 group-hover:scale-110" />
        </div>

        {unreadCount > 0 && (
          <div className={cn(
            "absolute -top-1 -right-1",
            "flex items-center justify-center",
            "h-6 w-6 rounded-full",
            "bg-red-500 text-white",
            "text-xs font-bold",
            "shadow-lg"
          )}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
      </button>
    </div>
  );
};